import { useIonRouter } from '@ionic/react';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  navigateShell,
  type NavigateShellOptions,
  type ShellIonRouter,
} from '@/core/lib/navigate-shell';
import { navigateShellDeepLink } from '@/core/lib/navigate-shell-deep-link';
import { canonicalizeShellPathname } from '@/core/router/app-paths';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

/**
 * Ionic-aware navigation for all in-shell routes (`/tabs/**`).
 */
export function useNavigateShell() {
  const ionRouter = useIonRouter();
  const location = useLocation();
  const navigate = useNavigate();
  const isNative = isCapacitorNative();

  const getContext = useCallback(
    () => ({
      currentPathname: canonicalizeShellPathname(location.pathname),
      currentSearch: location.search,
      isNative,
    }),
    [location.pathname, location.search, isNative],
  );

  const navigateShellTo = useCallback(
    (path: string, options?: NavigateShellOptions) => {
      void navigateShell(
        ionRouter as ShellIonRouter,
        path,
        getContext(),
        options,
      );
    },
    [ionRouter, getContext],
  );

  /** Sidebar / section switches — always uses tab reset semantics. */
  const navigateFromSidebar = useCallback(
    (path: string) => {
      const [rawPath = '', rawQuery = ''] = path.split('?');
      const targetPath = canonicalizeShellPathname(rawPath);
      const currentPath = canonicalizeShellPathname(location.pathname);
      const targetSearch = rawQuery ? `?${rawQuery}` : '';
      const currentSearch = location.search || '';

      if (targetPath === currentPath && targetSearch === currentSearch) {
        return;
      }

      // Same pathname, different query (e.g. Leads ↔ Events) — update search
      // without a full tab reset so Ionic stack stays put.
      if (targetPath === currentPath && targetSearch !== currentSearch) {
        navigate(
          { pathname: location.pathname, search: targetSearch },
          { replace: true },
        );
        return;
      }

      navigateShellTo(path, { intent: 'switchTab' });
    },
    [location.pathname, location.search, navigate, navigateShellTo],
  );

  const navigateShellDeepLinkTo = useCallback(
    (path: string, options?: Omit<NavigateShellOptions, 'coldLink'>) => {
      void navigateShellDeepLink(
        ionRouter as ShellIonRouter,
        path,
        getContext(),
        options,
      );
    },
    [ionRouter, getContext],
  );

  return {
    navigateShell: navigateShellTo,
    navigateFromSidebar,
    navigateShellDeepLink: navigateShellDeepLinkTo,
  };
}
